import { useCallback, useEffect, useRef, useState } from 'react';
import { layoutApi, type WidgetShow, type WidgetSource } from '../api';
import { addonsApi } from '../addons';
import { fromExt, fromPlex, RequestDialog, Tile, type DisplayItem } from '../components/Widgets';
import { useApp } from '../context';
import { addWidgetToHome } from '../layout';
import type { ExtItem } from '../api';
import { goBack, navigate } from '../router';

const PAGE = 60;

/**
 * Een hele lijst bekijken als raster (vanuit Collecties of door op de titel van een rij te klikken).
 * Laadt in stukken terwijl je naar beneden scrolt, tot alle titels er zijn (max. 500).
 */
export function ListView({ src }: { src: string }) {
  let parsed: { name: string; source: WidgetSource; show?: WidgetShow } | null = null;
  try {
    parsed = JSON.parse(decodeURIComponent(escape(atob(src)))) as { name: string; source: WidgetSource; show?: WidgetShow };
  } catch {
    parsed = null;
  }
  const [show, setShow] = useState<WidgetShow>(parsed?.show ?? 'all');
  const [added, setAdded] = useState(false);
  if (!parsed) return <main className="page"><p className="placeholder-row">Deze lijst kan niet worden geopend.</p></main>;
  const p = parsed;
  const filterable = p.source.kind !== 'plex' && p.source.kind !== 'addon';
  return (
    <main className="page mylist-page">
      <header className="page-head">
        <button className="btn" onClick={goBack}>‹ Terug</button>
        <h1>{p.name}</h1>
        <button className="btn btn-sm" disabled={added} onClick={() => void addWidgetToHome({ title: p.name, source: p.source, view: 'poster', show: 'all', limit: 20, enabled: true }).then(() => setAdded(true))}>
          {added ? '✓ Op Home' : '+ Zet op Home'}
        </button>
      </header>
      {filterable && (
        <div className="chip-wrap mylist-filter" data-focus-group>
          {([['all', 'Alles'], ['owned', 'In bibliotheek'], ['missing', 'Nog niet in bibliotheek']] as const).map(([id, label]) => (
            <button key={id} className="chip" aria-pressed={show === id} onClick={() => setShow(id)}>{label}</button>
          ))}
        </div>
      )}
      <FullGrid key={`${src}:${show}`} source={p.source} show={filterable ? show : 'all'} />
    </main>
  );
}

function FullGrid({ source, show }: { source: WidgetSource; show: WidgetShow }) {
  const { plex, libraries } = useApp();
  const [items, setItems] = useState<DisplayItem[]>([]);
  const [more, setMore] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requesting, setRequesting] = useState<ExtItem | null>(null);
  const offset = useRef(0);
  const sentinel = useRef<HTMLDivElement>(null);

  const loadMore = useCallback(async () => {
    if (busy || !more) return;
    setBusy(true);
    try {
      const at = offset.current;
      let page: DisplayItem[] = [];
      let hasMore = false;
      const s = source;
      if (s.kind === 'plex' && s.list !== 'watchlist') {
        let list: import('../plex').PlexItem[] = [];
        if (s.list === 'collection' && s.collection) list = await plex.collectionItems(s.collection, PAGE, at);
        else if (s.list === 'playlist' && s.playlist) list = await plex.playlistItems(s.playlist, PAGE, at);
        else {
          const lib = s.library ?? libraries.find((l) => l.type === 'movie')?.key ?? libraries.find((l) => l.type === 'show')?.key;
          if (lib) {
            const r = await plex.libraryItems(lib, {
              sort: 'addedAt:desc', unwatched: s.list === 'unwatched' ? true : undefined, genre: s.list === 'genre' ? s.genre : undefined, start: at, size: PAGE,
            });
            list = r.items;
            hasMore = at + list.length < r.total;
          }
        }
        if (s.list === 'collection' || s.list === 'playlist') hasMore = list.length === PAGE;
        page = list.map((i) => fromPlex(plex, i));
        offset.current = at + list.length;
      } else if (s.kind === 'addon') {
        const r = await addonsApi.catalog(s.addonId, s.catalog, { skip: at });
        page = r.items.map((m) => ({
          key: `addon:${s.addonId}:${m.id}`,
          title: m.name,
          sub: [m.type === 'series' ? 'Serie' : m.type === 'movie' ? 'Film' : null, m.releaseInfo].filter(Boolean).join(' · '),
          poster: m.poster ?? undefined,
          backdrop: m.background ?? undefined,
          ref: null,
          href: `/addon/${s.addonId}/${encodeURIComponent(m.type)}/${encodeURIComponent(m.id)}`,
        }));
        offset.current = at + r.items.length;
        hasMore = r.items.length > 0 && offset.current < 1000;
      } else {
        const r = await layoutApi.items(s, PAGE, show, at);
        page = r.items.map(fromExt);
        offset.current = at + PAGE;
        hasMore = !!r.more;
      }
      setItems((prev) => {
        const seen = new Set(prev.map((x) => x.key));
        return [...prev, ...page.filter((x) => !seen.has(x.key))];
      });
      setMore(hasMore && page.length > 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden mislukt');
      setMore(false);
    } finally {
      setBusy(false);
    }
  }, [busy, more, source, show, plex, libraries]);

  // Eerste stuk, en verder laden zodra je bij de onderkant komt
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) void loadMore();
    }, { rootMargin: '600px' });
    io.observe(el);
    return () => io.disconnect();
  }, [loadMore]);

  const onOpen = (it: DisplayItem) => {
    if (it.missing) setRequesting(it.missing);
    else if (it.href) navigate(it.href);
  };

  return (
    <>
      <p className="subtle list-count">{items.length} titels{more ? '…' : ''}</p>
      <div className="grid wall full-list" data-focus-group>
        {items.map((it) => <Tile key={it.key} it={it} kind="poster" onOpen={onOpen} />)}
      </div>
      {error && <p className="placeholder-row error">{error}</p>}
      {!items.length && !more && !error && <p className="placeholder-row">Geen titels met deze instellingen.</p>}
      <div ref={sentinel} className="list-sentinel">
        {more && <button className="btn" disabled={busy} onClick={() => void loadMore()}>{busy ? 'Laden…' : 'Meer laden'}</button>}
      </div>
      {requesting && <RequestDialog item={requesting} onClose={() => setRequesting(null)} />}
      <div className="footer-space" />
    </>
  );
}
